import { useState } from "react";
import { IconCircle, IconCircleFilled, IconDiamond, IconLock } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";

import { skills as allSkills } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import {
  MAX_SKILL_LEVEL,
  SKILL_UPGRADE_COST,
  canUpgradeSkill,
  isSkillUnlocked,
} from "../../engine/skills";
import { useGameStore } from "../../state/store";
import { canBuyTraining, trainingCost } from "../../engine/training";
import { formatMoney } from "../common/format";
import type { Skill } from "../../types";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { cx } from "../common/cx";
import { formatNumber } from "../common/format";
import { SkillUpgradeModal } from "./SkillUpgradeModal";
import styles from "./SkillsScreen.module.css";

const LEVELS = Array.from({ length: MAX_SKILL_LEVEL }, (_, i) => i);

export function SkillsScreen(): JSX.Element {
  const t = useLocalize();
  const player = useGameStore((state) => state.player);
  const training = useGameStore((state) => state.training);
  const buyTraining = useGameStore((state) => state.buyTraining);
  const trainingCostLabel = formatMoney(trainingCost(training.bought));
  const upgradeSkill = useGameStore((state) => state.upgradeSkill);
  const setActiveSkill = useGameStore((state) => state.setActiveSkill);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const points = formatNumber(player.skillPoints);
  const [confirmSkill, setConfirmSkill] = useState<Skill | null>(null);

  const learnedSkills = allSkills.filter((skill) => (player.skills[skill.id] ?? 0) > 0);
  const availableSkills = allSkills.filter((skill) => (player.skills[skill.id] ?? 0) === 0);

  const firstUpgradableId = availableSkills.find(
    (skill) =>
      skill.status === "active" &&
      isSkillUnlocked(skill.minLevel, player.level) &&
      canUpgradeSkill(player.skills, skill.id, player.skillPoints),
  )?.id;
  const firstTutorialSkillId =
    firstUpgradableId ?? availableSkills.find((skill) => skill.status !== "upcoming")?.id;

  return (
    <Panel
      title={<Trans>Навыки</Trans>}
      icon={<IconDiamond size={18} />}
      actions={
        <span className={styles.headActions}>
          <span className={styles.points}>
            <IconDiamond size={15} aria-hidden="true" />
            <Trans>Очки: {points}</Trans>
          </span>
          <Button
            variant="secondary"
            size="small"
            disabled={!canBuyTraining(player, training.bought)}
            onClick={buyTraining}
          >
            <Trans>Курс за {trainingCostLabel} (+1 очко)</Trans>
          </Button>
        </span>
      }
    >
      <p className={styles.earnHint}>
        <IconDiamond size={14} aria-hidden="true" />
        <Trans>
          Очко навыка даётся за каждое повышение уровня - уровень растёт от опыта (XP) за контракты.
          Или купи курс: деньги превращаются в очко, цена каждого следующего курса удваивается.
        </Trans>
      </p>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Trans>Изучено</Trans>
        </h2>
        {learnedSkills.length === 0 ? (
          <EmptyState title={<Trans>Ни один навык ещё не изучен</Trans>} />
        ) : (
          <div className={styles.grid}>
            {learnedSkills.map((skill) => {
              const level = player.skills[skill.id] ?? 0;
              const maxed = level >= MAX_SKILL_LEVEL;
              const canUpgrade = canUpgradeSkill(player.skills, skill.id, player.skillPoints);
              const currentEffect = skill.effects[level - 1];
              const nextEffect = skill.effects[level];
              const unlocked = isSkillUnlocked(skill.minLevel, player.level);
              const unlockLevel = skill.minLevel;
              const isUpcoming = skill.status === "upcoming";
              return (
                <Card
                  key={skill.id}
                  className={cx(styles.card, !unlocked && styles.locked)}
                  onClick={() => {
                    setActiveSkill(skill.id);
                    navigateTo("skill_details");
                  }}
                >
                  <div className={styles.head}>
                    <h3 className={styles.name}>
                      <button type="button" className={styles.nameButton}>
                        {t(skill.name)}
                      </button>
                    </h3>
                    <span className={styles.dots} aria-hidden="true">
                      {LEVELS.map((i) =>
                        i < level ? (
                          <IconCircleFilled key={i} size={12} className={styles.on} />
                        ) : (
                          <IconCircle key={i} size={12} className={styles.off} />
                        ),
                      )}
                    </span>
                  </div>
                  <p className={styles.description}>{t(skill.description)}</p>
                  {currentEffect && (
                    <p className={styles.effectNow}>
                      <span className={styles.effectLabel}><Trans>Сейчас:</Trans></span>{" "}
                      {t(currentEffect)}
                    </p>
                  )}
                  {nextEffect && (
                    <p className={styles.effectNext}>
                      <span className={styles.effectLabel}><Trans>Дальше:</Trans></span>{" "}
                      {t(nextEffect)}
                    </p>
                  )}
                  <div className={styles.foot}>
                    <span className={cx(styles.level, "mono")}>
                      {level} / {MAX_SKILL_LEVEL}
                    </span>
                    {isUpcoming ? (
                      <span className={styles.soonBadge}>
                        <Trans>Скоро</Trans>
                      </span>
                    ) : maxed ? (
                      <span className={styles.maxed}>
                        <Trans>Макс</Trans>
                      </span>
                    ) : !unlocked ? (
                      <span className={styles.lockCond}>
                        <IconLock size={14} aria-hidden="true" />
                        <Trans>Откроется на ур. {unlockLevel}</Trans>
                      </span>
                    ) : (
                      <Button
                        variant="primary"
                        size="small"
                        disabled={!canUpgrade}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmSkill(skill);
                        }}
                      >
                        <Trans>Прокачать ({SKILL_UPGRADE_COST} SP)</Trans>
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          <Trans>Доступно</Trans>
        </h2>
        <div className={styles.grid}>
          {availableSkills.map((skill) => {
            const level = player.skills[skill.id] ?? 0;
            const canUpgrade = canUpgradeSkill(player.skills, skill.id, player.skillPoints);
            const nextEffect = skill.effects[level];
            const isUpcoming = skill.status === "upcoming";
            const unlocked = isSkillUnlocked(skill.minLevel, player.level);
            const locked = !isUpcoming && !unlocked;
            const unlockLevel = skill.minLevel;
            return (
              <Card
                key={skill.id}
                className={cx(styles.card, locked && styles.locked)}
                data-tutorial={skill.id === firstTutorialSkillId ? "skill-card" : undefined}
                onClick={() => {
                  setActiveSkill(skill.id);
                  navigateTo("skill_details");
                }}
              >
                <div className={styles.head}>
                  <h3 className={styles.name}>
                      <button type="button" className={styles.nameButton}>
                        {t(skill.name)}
                      </button>
                    </h3>
                  {isUpcoming ? (
                    <span className={styles.soonBadge}>
                      <Trans>Скоро</Trans>
                    </span>
                  ) : (
                    <span className={styles.dots} aria-hidden="true">
                      {LEVELS.map((i) =>
                        i < level ? (
                          <IconCircleFilled key={i} size={12} className={styles.on} />
                        ) : (
                          <IconCircle key={i} size={12} className={styles.off} />
                        ),
                      )}
                    </span>
                  )}
                </div>
                <p className={styles.description}>{t(skill.description)}</p>
                {nextEffect && (
                  <p className={styles.effectNext}>
                    <span className={styles.effectLabel}><Trans>Дальше:</Trans></span>{" "}
                    {t(nextEffect)}
                  </p>
                )}
                {!isUpcoming && (
                  <div className={styles.foot}>
                    <span className={cx(styles.level, "mono")}>
                      {level} / {MAX_SKILL_LEVEL}
                    </span>
                    {unlocked ? (
                      <Button
                        variant="primary"
                        size="small"
                        disabled={!canUpgrade}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmSkill(skill);
                        }}
                      >
                        <Trans>Прокачать ({SKILL_UPGRADE_COST} SP)</Trans>
                      </Button>
                    ) : (
                      <span className={styles.lockCond}>
                        <IconLock size={14} aria-hidden="true" />
                        <Trans>Откроется на ур. {unlockLevel}</Trans>
                      </span>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {confirmSkill && (
        <SkillUpgradeModal
          skill={confirmSkill}
          currentLevel={player.skills[confirmSkill.id] ?? 0}
          skillPoints={player.skillPoints}
          onConfirm={() => {
            upgradeSkill(confirmSkill.id);
            setConfirmSkill(null);
          }}
          onClose={() => setConfirmSkill(null)}
        />
      )}
    </Panel>
  );
}
